import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 30000 });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("ubbs_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Retry transient errors (cold-start 404, 502, 503, 504, network errors).
// 4 attempts with exponential backoff: 1.5s, 3s, 6s, 12s ≈ 22s total budget.
// Skips retry for 401/403/4xx (real auth/validation errors) and non-GET writes.
const MAX_RETRIES = 4;
const RETRY_STATUSES = new Set([404, 408, 425, 429, 500, 502, 503, 504]);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err?.config;
    const status = err?.response?.status;
    const isNetwork = !err?.response; // no response = network error / DNS / timeout

    // Decide if this is a "warming up" failure worth retrying
    const retryable =
      cfg &&
      (isNetwork || (status && RETRY_STATUSES.has(status))) &&
      // Only auto-retry idempotent reads or login (POST /auth/login)
      ((cfg.method || "get").toLowerCase() === "get" ||
        cfg.url?.endsWith("/auth/login") ||
        cfg.url?.endsWith("/auth/me"));

    if (retryable) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (cfg.__retryCount <= MAX_RETRIES) {
        // Exponential backoff: 1.5s, 3s, 6s, 12s
        const delay = 1500 * Math.pow(2, cfg.__retryCount - 1);
        await sleep(delay);
        return api(cfg);
      }
      // Out of retries — replace the message with a friendlier one
      const friendly = "Server is waking up. Please try again in a few seconds.";
      if (err?.response) {
        // err.response.data may be a string (HTML/text from a proxy) or an object.
        // Always overwrite with a JSON object so formatApiError can read .detail.
        err.response.data = { detail: friendly };
      } else {
        err.message = friendly;
      }
    }

    if (status === 401) {
      localStorage.removeItem("ubbs_token");
      localStorage.removeItem("ubbs_user");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  },
);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(d);
}

export default api;
